/**
 * 房间信息
 */
import React, { memo, useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';

import ContentBlock from '@/components/Reservation/ContentBlock';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import IntlContext from '@/components/Intl/IntlContext';
import { OCC_TYPE_MAP, OCC_VALUE_MAP } from '@/config/constant';
import { format2FullTimeStr } from '@/utils/date';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { handleRecordItemChange } from '@/redux/actions/reservation/cancelReserve';
import {
  ReducerState,
  storeName,
  RentRecordInfo,
} from '@/redux/reducers/reservation/cancelReserve';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentRecordList, channelList } = state;
  return { rentRecordList, channelList };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callItemChange: handleRecordItemChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const { rentRecordList, channelList, callItemChange } = props;

  const channelNameMap = useMemo(() => {
    const result = [];
    if (channelList && channelList.length) {
      channelList.forEach((item) => {
        result[item.otaBillType || item.type] = item.name;
      });
    }
    return result;
  }, [channelList]);

  const intlCtx = useContext(IntlContext);

  // 渲染渠道名称
  const renderChannelName = (channelCode) => {
    return channelNameMap[channelCode] || channelCode;
  };

  const handlePriceChange: (liquidatedDamages: string, record: RentRecordInfo) => void = (
    liquidatedDamages,
    record,
  ) => {
    // if (PRICE_REG.test(liquidatedDamages)) {
    callItemChange(record.indexId, { liquidatedDamages });
    // }
  };

  const renderPriceItem: (value: string, record: RentRecordInfo) => React.ReactNode = (
    value,
    record,
  ) => {
    return (
      <InputWithCoupon
        withCoupon={false}
        value={value}
        prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
        onChange={(val) => handlePriceChange(val, record)}
      />
    );
  };

  const tableColumns: ColumnsType<RentRecordInfo> = [
    {
      title: intl.get('app_constants_room_type'),
      dataIndex: 'roomTypeName',
      key: 'roomTypeName',
    },
    {
      title: intl.get('drawer_reserve_cancel_room_column_channel'),
      dataIndex: 'priceChannel',
      key: 'priceChannel',
      render: renderChannelName,
    },
    {
      title: intl.get('app_constants_room_no'),
      dataIndex: 'roomNo',
      key: 'roomNo',
      width: 140,
      render: (val) => val || intl.get('app_reservation_room_queue_no'),
    },
    {
      title: intl.get('app_reservation_occ_type'),
      dataIndex: 'occType',
      key: 'occType',
      render: (val) => OCC_TYPE_MAP[val] || '-',
    },
    {
      title: intl.get('app_reservation_eta_time'),
      dataIndex: 'expectedArrivalTime',
      key: 'expectedArrivalTime',
      render: (val) => format2FullTimeStr(val),
    },
    {
      title: intl.get('app_reservation_etd_time'),
      dataIndex: 'expectedDepartureTime',
      key: 'expectedDepartureTime',
      render: (val) => format2FullTimeStr(val),
    },
    {
      title: intl.get('app_reservation_occ_duration'),
      dataIndex: 'occDuration',
      key: 'occDuration',
      render: (val, record) => `${val}${OCC_VALUE_MAP[record.occType]}`,
    },
    {
      title: intl.get('drawer_reserve_cancel_room_column_damages'),
      dataIndex: 'liquidatedDamages',
      key: 'liquidatedDamages',
      align: 'right',
      render: (value, record) => renderPriceItem(value, record),
    },
  ];

  return (
    <ContentBlock title={intl.get('drawer_reserve_cancel_room_block_title')}>
      <Table
        pagination={false}
        className={styles.rentRoomContainer}
        rowKey="indexId"
        columns={tableColumns}
        dataSource={rentRecordList}
      />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
